/**
 * Ingredient Banner — Section 1
 * ------------------------------------------------------------
 * Responsibilities of this file, and ONLY these:
 *   1. Track which ingredient card is active inside this section.
 *   2. Reflect that state visually (`.is-active` class, aria-pressed).
 *   3. Broadcast the change via a CustomEvent so any other section
 *      (e.g. a future "Ingredient Details" / Section 2) can react,
 *      without this section knowing anything about who is listening.
 *
 * Public contract for other sections (do not change without
 * updating every listener):
 *
 *   Event name:  "ingredientChanged"
 *   Dispatched on: document
 *   detail: {
 *     ingredient: string,   // e.g. "oat", "aloe", "panthenol", "beta"
 *     sectionId: string,    // the Shopify section.id this came from
 *     name: string,         // human readable ingredient name
 *     isInitial: boolean    // true only for the sync-on-load event
 *   }
 *
 *   Registry:    window.VenatiIngredients
 *     .getActive(sectionId?) -> { ingredient, name } | null
 *     .getAll(sectionId?)    -> [{ ingredient, name, image }]
 *
 *   If `sectionId` is omitted, the registry falls back to the
 *   first Ingredient Banner section found on the page — this
 *   keeps Section 2 simple for the common single-instance case
 *   while still supporting multiple instances if ever needed.
 *
 * No localStorage. No URL params. No page reload. State lives
 * only in memory for the lifetime of the page.
 */

(function () {
  'use strict';

  var EVENT_NAME = 'ingredientChanged';
  var SELECTOR = '[data-ingredient-banner]';
  var CARD_SELECTOR = '[data-ingredient-card]';

  /** sectionId -> { activeId, cards: Map(id -> {name, image}) } */
  var registry = new Map();

  function firstSectionId() {
    var iterator = registry.keys().next();
    return iterator.done ? null : iterator.value;
  }

  function dispatchChange(sectionId, ingredientId, name, isInitial) {
    document.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        bubbles: true,
        detail: {
          ingredient: ingredientId,
          sectionId: sectionId,
          name: name || null,
          isInitial: !!isInitial
        }
      })
    );
  }

  function setActiveCard(root, card, options) {
    options = options || {};
    var sectionId = root.getAttribute('data-section-id');
    var ingredientId = card.getAttribute('data-ingredient-id');
    var name = card.getAttribute('data-ingredient-name');

    var cards = root.querySelectorAll(CARD_SELECTOR);
    for (var i = 0; i < cards.length; i++) {
      var isThisCard = cards[i] === card;
      cards[i].classList.toggle('is-active', isThisCard);
      cards[i].setAttribute('aria-pressed', String(isThisCard));
    }

    var entry = registry.get(sectionId);
    if (entry) {
      entry.activeId = ingredientId;
    }

    if (options.silent) {
      return;
    }

    dispatchChange(sectionId, ingredientId, name, !!options.isInitial);
  }

  function buildRegistryEntry(root) {
    var cards = root.querySelectorAll(CARD_SELECTOR);
    var cardMap = new Map();
    var activeId = null;

    cards.forEach(function (card) {
      var id = card.getAttribute('data-ingredient-id');
      var name = card.getAttribute('data-ingredient-name');
      var img = card.querySelector('img');

      cardMap.set(id, {
        ingredient: id,
        name: name,
        image: img ? img.getAttribute('src') : null
      });

      if (card.classList.contains('is-active')) {
        activeId = id;
      }
    });

    if (!activeId && cards.length) {
      activeId = cards[0].getAttribute('data-ingredient-id');
    }

    return { activeId: activeId, cards: cardMap };
  }

  function initSection(root) {
    if (!root || root.__ingredientBannerInit) {
      return;
    }
    root.__ingredientBannerInit = true;

    var sectionId = root.getAttribute('data-section-id');
    registry.set(sectionId, buildRegistryEntry(root));

    root.addEventListener('click', function (event) {
      var card = event.target.closest(CARD_SELECTOR);
      if (!card || !root.contains(card)) {
        return;
      }
      if (card.classList.contains('is-active')) {
        return;
      }
      setActiveCard(root, card);
    });

    // Sync whoever is already listening (or listens shortly after)
    // with the default active state, without requiring a click.
    var entry = registry.get(sectionId);
    if (entry && entry.activeId) {
      var activeCard = root.querySelector(
        '[data-ingredient-id="' + cssEscape(entry.activeId) + '"]'
      );
      if (activeCard) {
        // Defer so Section 2's own init (which may run right after
        // this script tag) has a chance to attach its listener first.
        window.requestAnimationFrame(function () {
          setActiveCard(root, activeCard, { isInitial: true });
        });
      }
    }
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function initAll(context) {
    context = context || document;
    var sections = context.querySelectorAll(SELECTOR);
    sections.forEach(initSection);
  }

  // Public, read-only registry API for any section that loads after this one.
  window.VenatiIngredients = window.VenatiIngredients || {
    getActive: function (sectionId) {
      var id = sectionId || firstSectionId();
      var entry = registry.get(id);
      if (!entry || !entry.activeId) {
        return null;
      }
      var card = entry.cards.get(entry.activeId);
      return card ? { ingredient: card.ingredient, name: card.name } : null;
    },
    getAll: function (sectionId) {
      var id = sectionId || firstSectionId();
      var entry = registry.get(id);
      if (!entry) {
        return [];
      }
      return Array.from(entry.cards.values());
    }
  };

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  // Shopify theme editor support: re-init when this section is
  // added/re-rendered without a full page reload.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.matches
      ? (event.target.matches(SELECTOR) ? event.target : event.target.querySelector(SELECTOR))
      : null;
    if (root) {
      root.__ingredientBannerInit = false;
      initSection(root);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.matches
      ? (event.target.matches(SELECTOR) ? event.target : event.target.querySelector(SELECTOR))
      : null;
    if (root) {
      var sectionId = root.getAttribute('data-section-id');
      registry.delete(sectionId);
    }
  });

  // In case this script executes after DOMContentLoaded already fired
  // (e.g. loaded dynamically), initialize immediately as a fallback.
  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initAll(document);
  }
})();
